import {UseQueryOptions} from 'react-query';
import {AxiosError, AxiosResponse} from 'axios';
import {ServiceError} from '../../../../../hooks/error';
import {useCustomMutate, useCustomPostQuery, useCustomQuery} from '../../../../../hooks/query';
import {
  AdmissionExamLevelResponse,
  CandidateRegisteredExamListingResponse,
  CandidateRegisteredExamResponse,
  CandidateTransactionHistoryListingResponse,
  CreateExamRegistrationRequest,
  CreateExamRegistrationResponse,
  CreateExamWithdrawalRequest,
  CreateNonExamRegistrationRequest,
  ExamCandidateDetails,
  ExamDownloadSummaryResponse,
  ExamLevelResponse,
  ExamProceedToPaymentRequestBody,
  ExamProceedToPaymentResponseBody,
  ExamRegisteredSummaryResponse,
  ExamRegistrationEligibilityRequest,
  ExamRegistrationEligibilityResponse,
  ExamRegistrationResponse,
  ExamReviewResponse,
  ExamSaveSubjectRegistrationDraftRequest,
  ExamSubjectAmendmentDraftRequest,
  ExamSubjectsListResponseBody,
  ExamTransactionInfo,
  GetWithdrawalDraftResponse,
  GetWithdrawalResponse,
  NonExamRegistrationEligibilityResponse,
  ParentParticularDetails,
  PaymentLinkRequest,
  PutWithdrawalDetailsRequestData,
  RegisteredSubjectResponse,
  SearchExamSubjectsListSchema,
  SubjectAmendmentReviewResponse,
  SubjectAmendmentSummaryResponse,
  SubjectRegistrationDraftRequest,
  SubjectValidationResponse,
  TokenOnlyRequest,
  UpdateSubjectAmendmentDraftRequest,
  UpdateSubjectRegistrationDraftRequest,
  ViewCandidateRegisteredSubject,
  ViewSelectedSubjectsResponseBody,
  ViewSubjectDetailResponse,
  WithdrawalPostUploadDocumentsRequest,
  WithdrawalReason,
  WithdrawalSubmittedDetails,
  WithdrawDetails,
} from '../../model';
import {
  deleteExamSubjectRegistrationDraft,
  deleteRegisteredExamDraft,
  getAdmissionExamLevel,
  getCandidateExamAmendmentDownloadSummary,
  getCandidateExamDownloadSummary,
  getCandidateRegisteredExamAmendment,
  getCandidateRegisteredSubject,
  getCeqRequired,
  getCeqTypes,
  getExamCandidateProfile,
  getExamLevelAmendment,
  getExamLevelWithdrawal,
  getExamRegistration,
  getExamSeries,
  getExamTransactionInfo,
  getExamTypes,
  getLanguages,
  getParentParticularProfile,
  getPendingExamRegistration,
  getPendingNonNationalExamRegistration,
  getRegisteredAmendmentSummary,
  getRegisteredExam,
  getRegisteredExamListing,
  getRegisteredExamSummary,
  getRegisteredSubjects,
  getReviewAmendmentSummary,
  getSelectedSubjects,
  getSubjectDetail,
  getSubmittedWithdrawal,
  getTransactionHistoryListing,
  getWithdrawal,
  getWithdrawalAcknowledgement,
  getWithdrawalDownloadPdf,
  getWithdrawalDraft,
  getWithdrawalDraftDateTime,
  getWithdrawalDropDown,
  getWithdrawDetails,
  postCandidateExamAmendmentPayment,
  postCandidateExamPayment,
  postCreateExamAmendment,
  postCreateExamRegistration,
  postCreateExamWithdrawal,
  postCreateNonExamRegistration,
  postExamLevel,
  postExamRegistrationCandidateProfile,
  postExamSubjectAmendmentDraft,
  postExamSubjectRegistrationDraft,
  postExamSubjects,
  postSubjectValidation,
  postWithdrawalUploadDocuments,
  putConfirmWithdraw,
  putDraftSubjectWithdrawal,
  putExamSubjectRegistrationDraft,
  putUndoDraftSubjectWithdrawal,
  putUpdateExamWithdrawalDetails,
  putUpdateRegistrationSubjectAmendment,
} from '../api-services/api';
import {
  DELETE_EXAM_REGISTRATION_CANDIDATE_DRAFT,
  DELETE_EXAM_SUBJECT_REGISTRATION_DRAFT,
  GET_CANDIDATE_REGISTERED_EXAM,
  GET_CANDIDATE_REGISTERED_EXAM_AMENDMENT,
  GET_CANDIDATE_REGISTERED_SUBJECT,
  GET_CANDIDATE_TRANSACTION_HISTORY,
  GET_EXAM__AMENDMENT_SUMMARY_PDF,
  GET_EXAM_AMENDMENT_SUMMARY,
  GET_EXAM_CANDIDATE_PROFILE,
  GET_EXAM_LEVEL,
  GET_EXAM_LEVEL_WITHDRAWAL,
  GET_EXAM_REGISTRATION,
  GET_EXAM_REGISTRATION_SUMMARY,
  GET_EXAM_SERIES,
  GET_EXAM_SUBJECTS,
  GET_EXAM_SUMMARY_PDF,
  GET_EXAM_TRANSACTION_INFO,
  GET_EXAM_TYPES,
  GET_EXAM_WITHDRAWAL_DROPDOWN,
  GET_LANGUAGES,
  GET_PARENT_PARTICULAR_PROFILE,
  GET_PENDING_REGISTRATION,
  GET_REGISTERED_SUBJECTS,
  GET_REVIEW_EXAM_AMENDMENT,
  GET_SELECTED_SUBJECTS,
  GET_SUBJECT_DETAIL,
  GET_SUBMITTED_WITHDRAWAL,
  GET_WITHDRAW_DETAILS,
  GET_WITHDRAWAL_ACKNOWLEDGEMENT,
  GET_WITHDRAWAL_DOWNLOAD_PDF,
  GET_WITHDRAWAL_DRAFT,
  GET_WITHDRAWAL_TIMESTAMP,
  POST_CANDIDATE_EXAM_PAYMENT,
  POST_CREATE_EXAM_AMENDMENT,
  POST_CREATE_EXAM_REGISTRATION,
  POST_CREATE_NON_EXAM_REGISTRATION,
  POST_EXAM_LEVEL,
  POST_EXAM_REGISTRATION_CANDIDATE_PROFILE,
  POST_EXAM_SUBJECT_AMENDMENT_DRAFT,
  POST_EXAM_SUBJECT_REGISTRATION_DRAFT,
  POST_EXAM_WITHDRAWAL_UPLOAD_DOCUMENT,
  POST_SUBJECT_VALIDATION,
  PUT_CONFIRM_WITHDRAW,
  PUT_DRAFT_SUBJECT_WITHDRAWAL,
  PUT_EXAM_SUBJECT_AMENDMENT_DRAFT,
  PUT_EXAM_SUBJECT_REGISTRATION_DRAFT,
  PUT_EXAM_WITHDRAWAL_DETAILS,
} from './keys';
import {GenericErrorResponse, KeyValueResponse} from '../../../../model';
import {ConfigurationType} from '../../../config/model';
import {ApiPathParam} from '../../../../../models/SearchParamSchema';
import {GET_ADMISSION_EXAM_LEVEL, GET_CEQ_REQUIRED, GET_CEQ_TYPES} from '../api-services/routes';

/** to get resit subject
 * @return => array
 * */
export const useResitRegisteredExamQuery = (
    queryOptions?: UseQueryOptions<CandidateRegisteredExamResponse[], ServiceError> | undefined
) =>
    useCustomQuery<CandidateRegisteredExamResponse[]>(
        GET_CANDIDATE_REGISTERED_EXAM,
        () => getRegisteredExam().then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const useGetPendingExamRegistration = (
    params: CreateExamRegistrationRequest,
    queryOptions?: UseQueryOptions<ExamRegistrationEligibilityResponse, ServiceError> | undefined
) =>
    useCustomQuery<ExamRegistrationEligibilityResponse>(
        [GET_PENDING_REGISTRATION, params],
        () => getPendingExamRegistration(params).then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const useGetExamTypes = (
    queryOptions?: UseQueryOptions<KeyValueResponse[], ServiceError> | undefined
) =>
    useCustomQuery<KeyValueResponse[]>(
        GET_EXAM_TYPES,
        () => getExamTypes().then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const useGetExamRegistration = (
    uuid: string,
    queryOptions?: UseQueryOptions<ExamRegistrationResponse, ServiceError> | undefined
) =>
    useCustomQuery<ExamRegistrationResponse>(
        [GET_EXAM_REGISTRATION, uuid],
        () => getExamRegistration(uuid).then((res) => res.data),
        queryOptions,
        true,
        true
    );

export const usePostCreateExamRegistration = (
    onSuccess: (res: any) => void,
    onError: (res: any) => void
) =>
    useCustomMutate<AxiosResponse<CreateExamRegistrationResponse>,
        CreateExamRegistrationRequest,
        AxiosError<GenericErrorResponse>>(POST_CREATE_EXAM_REGISTRATION, postCreateExamRegistration, {
      onSuccess,
      onError,
    });

export const usePostExamProceedToPayment = () =>
    useCustomMutate<AxiosResponse<ExamProceedToPaymentResponseBody>,
        ExamProceedToPaymentRequestBody,
        AxiosError<GenericErrorResponse>>(POST_CANDIDATE_EXAM_PAYMENT, postCandidateExamPayment, {}, false, false);

export const useGetExamTransactionInfo = (uuid = '') =>
    useCustomQuery<ExamTransactionInfo>(GET_EXAM_TRANSACTION_INFO, () =>
        getExamTransactionInfo(uuid).then((response) => response.data)
    );

export const useGetExamCandidateProfile = (
    uuid: string,
    queryOptions?: UseQueryOptions<ExamCandidateDetails, ServiceError> | undefined
) =>
    useCustomQuery<ExamCandidateDetails>(
        GET_EXAM_CANDIDATE_PROFILE,
        () => getExamCandidateProfile(uuid).then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const usePostExamRegistrationCandidateProfile = (
    uuid: string,
    onSuccess: (res: AxiosResponse<ExamReviewResponse>, variable: PaymentLinkRequest | null) => void,
    onError: (res: AxiosError<GenericErrorResponse>, variable: PaymentLinkRequest | null) => void,
    onSettled?: (
        data: AxiosResponse<ExamReviewResponse> | undefined,
        error: AxiosError<AxiosError<GenericErrorResponse>> | null,
        variables: PaymentLinkRequest | null
    ) => void
) =>
    useCustomMutate<AxiosResponse<ExamReviewResponse>,
        PaymentLinkRequest | null,
        AxiosError<GenericErrorResponse>>(
        [POST_EXAM_REGISTRATION_CANDIDATE_PROFILE, uuid],
        (csrfToken, data) => postExamRegistrationCandidateProfile(csrfToken, uuid, data),
        {
          onSettled,
          onSuccess,
          onError,
        },
        false,
        false
    );

export const useGetCandidateDownloadSummary = (
    onSuccess: (download: ExamDownloadSummaryResponse) => void,
    onError: (data: AxiosError) => void,
    uuid = ''
) =>
    useCustomQuery(
        GET_EXAM_SUMMARY_PDF,
        () => getCandidateExamDownloadSummary(uuid).then((res) => res.data),
        {
          enabled: false,
          onSuccess,
          onError,
        },
        true,
        false
    );

export const useGetExamRegistrationsListing = (
    examUuid: string | undefined,
    onSuccess?: (res: CandidateRegisteredExamListingResponse[]) => void,
    onError?: (data: AxiosError) => void
) =>
    useCustomQuery<CandidateRegisteredExamListingResponse[]>(
        GET_EXAM_REGISTRATION,
        () => getRegisteredExamListing(examUuid).then((res) => res.data),
        {onSuccess, onError},
        false,
        false
    );

export const useGetExamRegistrationSummary = (uuid = '', onError?: (data: AxiosError) => void) =>
    useCustomQuery<ExamRegisteredSummaryResponse>(
        GET_EXAM_REGISTRATION_SUMMARY,
        () => getRegisteredExamSummary(uuid).then((res) => res.data),
        {onError},
        false,
        false
    );

export const useDeleteExamRegistrationDraft = (
    onSuccess?: (res: any) => void,
    onError?: (res: any) => void,
    onSettled?: (res: any) => void
) =>
    useCustomMutate<AxiosResponse, string>(
        DELETE_EXAM_REGISTRATION_CANDIDATE_DRAFT,
        (csrfToken, uuid) => deleteRegisteredExamDraft(csrfToken, uuid),
        {
          onSuccess,
          onError,
          onSettled,
        }
    );

export const useGetSelectedSubjects = (uuid = '') =>
    useCustomQuery<ViewSelectedSubjectsResponseBody[]>(
        [GET_SELECTED_SUBJECTS, uuid],
        () => getSelectedSubjects(uuid).then((res) => res.data),
        {},
        false,
        false
    );

export const usePostExamSubjectRegistrationDraft = (
    onSuccess: (res: AxiosResponse, variable: ExamSaveSubjectRegistrationDraftRequest) => void,
    onError: (
        res: AxiosError<GenericErrorResponse>,
        variable: ExamSaveSubjectRegistrationDraftRequest
    ) => void,
    onSettled?: (res: any) => void
) =>
    useCustomMutate<AxiosResponse,
        ExamSaveSubjectRegistrationDraftRequest,
        AxiosError<GenericErrorResponse>>(POST_EXAM_SUBJECT_REGISTRATION_DRAFT, postExamSubjectRegistrationDraft, {
      onSuccess,
      onError,
      onSettled,
    });

export const usePostExamSubjects = (
    onSuccess: (res: AxiosResponse<ExamSubjectsListResponseBody>) => void,
    onError: (res: AxiosError<GenericErrorResponse>) => void,
    onSettled?: (res: any) => void
) =>
    useCustomMutate<AxiosResponse<ExamSubjectsListResponseBody>,
        SearchExamSubjectsListSchema,
        AxiosError<GenericErrorResponse>>(GET_EXAM_SUBJECTS, postExamSubjects, {
      onSuccess,
      onError,
      onSettled,
    });

export const usePutExamSubjects = (
    onSuccess: (res: AxiosResponse, variable: UpdateSubjectRegistrationDraftRequest) => void,
    onError: (
        res: AxiosError<GenericErrorResponse>,
        variable: UpdateSubjectRegistrationDraftRequest
    ) => void
) =>
    useCustomMutate<AxiosResponse,
        UpdateSubjectRegistrationDraftRequest,
        AxiosError<GenericErrorResponse>>(PUT_EXAM_SUBJECT_REGISTRATION_DRAFT, putExamSubjectRegistrationDraft, {
      onSuccess,
      onError,
    });

export const useDeleteExamSubjects = (
    onSuccess: (res: AxiosResponse, variable: SubjectRegistrationDraftRequest) => void,
    onError: (
        res: AxiosError<GenericErrorResponse>,
        variable: SubjectRegistrationDraftRequest
    ) => void,
    onSettled?: () => void
) =>
    useCustomMutate<AxiosResponse, SubjectRegistrationDraftRequest, AxiosError<GenericErrorResponse>>(
        DELETE_EXAM_SUBJECT_REGISTRATION_DRAFT,
        deleteExamSubjectRegistrationDraft,
        {
          onSuccess,
          onError,
          onSettled,
        }
    );

export const useGetSubjectDetail = (applicationUuid: string, subjectCode: string) =>
    useCustomQuery<ViewSubjectDetailResponse>(
        [GET_SUBJECT_DETAIL, subjectCode],
        () => getSubjectDetail(applicationUuid, subjectCode).then((res) => res.data),
        {
          enabled: !!subjectCode,
          refetchOnWindowFocus: false,
        }
    );

export const usePostExamLevel = (payload: ExamRegistrationEligibilityRequest) =>
    useCustomPostQuery<AxiosResponse<ExamReviewResponse>, ExamRegistrationEligibilityRequest>(
        POST_EXAM_LEVEL,
        (csrfToken, data) => postExamLevel(csrfToken, data).then((res) => res.data),
        {
          enabled: !!payload.examType,
        },
        payload
    );

export const useGetExamSeries = () =>
    useCustomQuery<ConfigurationType[]>(
        GET_EXAM_SERIES,
        () => getExamSeries().then((res) => res.data),
        {
          refetchOnWindowFocus: false,
        }
    );

export const useGetLanguages = () =>
    useCustomQuery<ConfigurationType[]>(GET_LANGUAGES, () => getLanguages().then((res) => res.data), {
      refetchOnWindowFocus: false,
    });

export const useGetCandidateRegisteredSubject = (
    applicationUuid: string,
    examRegistrationDetailsUuid: string
) =>
    useCustomQuery<ViewCandidateRegisteredSubject>(
        [GET_CANDIDATE_REGISTERED_SUBJECT, examRegistrationDetailsUuid],
        () =>
            getCandidateRegisteredSubject(applicationUuid, examRegistrationDetailsUuid).then(
                (res) => res.data
            ),
        {
          refetchOnWindowFocus: false,
          enabled: !!examRegistrationDetailsUuid,
        }
    );

export const useGetCandidateRegisteredExamAmendment = (
    applicationUuid: string,
    subjectCode: string
) =>
    useCustomQuery<ViewCandidateRegisteredSubject>(
        [GET_CANDIDATE_REGISTERED_EXAM_AMENDMENT],
        () => getCandidateRegisteredExamAmendment(applicationUuid, subjectCode).then((res) => res.data),
        {
          refetchOnWindowFocus: false,
          enabled: !!subjectCode,
        }
    );

export const usePostCreateExamAmendment = (
    onSuccess: (res: any) => void,
    onError: (res: any) => void
) =>
    useCustomMutate(POST_CREATE_EXAM_AMENDMENT, postCreateExamAmendment, {
      onSuccess: (res) => onSuccess(res),
      onError: (err) => onError(err),
    });

export const useGetRegisteredSubject = (subjectCode: string) =>
    useCustomQuery<RegisteredSubjectResponse>(
        GET_REGISTERED_SUBJECTS,
        () => getRegisteredSubjects(subjectCode).then((res) => res.data),
        {}
    );

export const useGetExamLevelAmendment = () =>
    useCustomQuery<ExamLevelResponse>(
        GET_EXAM_LEVEL,
        () => getExamLevelAmendment().then((res) => res.data),
        {}
    );

export const usePutDrafSubjectWithdrawal = (
    onSuccess: (res: any, variable: any) => void,
    onError: (res: any, variable: any) => void
) =>
    useCustomMutate(
        PUT_DRAFT_SUBJECT_WITHDRAWAL,
        putDraftSubjectWithdrawal,
        {
          onSuccess,
          onError,
        },
        true,
        false
    );

export const usePutUndoDrafSubjectWithdrawal = (
    onSuccess: (res: any, variable: any) => void,
    onError: (res: any, variable: any) => void
) =>
    useCustomMutate(PUT_DRAFT_SUBJECT_WITHDRAWAL, putUndoDraftSubjectWithdrawal, {
      onSuccess,
      onError,
    });

export const usePostExamSubjectAmendmentDraft = (
    onSuccess: (res: AxiosResponse, variable: ExamSubjectAmendmentDraftRequest) => void,
    onError: (
        res: AxiosError<GenericErrorResponse>,
        variable: ExamSubjectAmendmentDraftRequest
    ) => void,
    onSettled?: (res: any) => void
) =>
    useCustomMutate<AxiosResponse,
        ExamSubjectAmendmentDraftRequest,
        AxiosError<GenericErrorResponse>>(POST_EXAM_SUBJECT_AMENDMENT_DRAFT, postExamSubjectAmendmentDraft, {
      onSuccess,
      onError,
      onSettled,
    });

export const usePutExamAmendment = (
    onSuccess: (res: AxiosResponse, variable: UpdateSubjectAmendmentDraftRequest) => void,
    onError: (
        res: AxiosError<GenericErrorResponse>,
        variable: UpdateSubjectAmendmentDraftRequest
    ) => void
) =>
    useCustomMutate<AxiosResponse,
        UpdateSubjectAmendmentDraftRequest,
        AxiosError<GenericErrorResponse>>(PUT_EXAM_SUBJECT_AMENDMENT_DRAFT, putUpdateRegistrationSubjectAmendment, {
      onSuccess,
      onError,
    });

export const useGetReviewExamAmendment = (
    uuid = '',
    options?: {
      onSuccess?: (res: SubjectAmendmentReviewResponse) => void;
      onError?: (res: AxiosError<GenericErrorResponse>) => void;
      onSettled?: (
          data: SubjectAmendmentReviewResponse | undefined,
          error: GenericErrorResponse | null
      ) => void;
      token?: string;
      enabled?: boolean;
    }
) =>
    useCustomQuery<SubjectAmendmentReviewResponse>(
        GET_REVIEW_EXAM_AMENDMENT,
        () => getReviewAmendmentSummary(uuid, options?.token).then((res) => res.data),
        {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
          onSettled: options?.onSettled,
          enabled: options?.enabled,
        },
        false,
        false
    );

export const useGetExamAmendmentSummary = (
    onSuccess: (res: SubjectAmendmentSummaryResponse) => void,
    onError?: (res: AxiosError<GenericErrorResponse>) => void,
    uuid = ''
) =>
    useCustomQuery<SubjectAmendmentSummaryResponse>(
        GET_EXAM_AMENDMENT_SUMMARY,
        () => getRegisteredAmendmentSummary(uuid).then((res) => res.data),
        {onSuccess, onError},
        false,
        false
    );

export const usePostPayExamAmendment = () =>
    useCustomMutate<AxiosResponse<ExamProceedToPaymentResponseBody>,
        ExamProceedToPaymentRequestBody,
        AxiosError<GenericErrorResponse>>(POST_CANDIDATE_EXAM_PAYMENT, postCandidateExamAmendmentPayment, {}, false, false);

export const useGetExamAmendmentDownloadSummary = (
    onSuccess: (download: ExamDownloadSummaryResponse) => void,
    onError: (data: AxiosError) => void,
    uuid = ''
) =>
    useCustomQuery(
        GET_EXAM__AMENDMENT_SUMMARY_PDF,
        () => getCandidateExamAmendmentDownloadSummary(uuid).then((res) => res.data),
        {
          enabled: false,
          onSuccess,
          onError,
        },
        true,
        false
    );

export const usePostSubjectValidation = (
    onSuccess: (res: AxiosResponse) => void,
    onError: (res: AxiosError<GenericErrorResponse>) => void
) =>
    useCustomMutate<AxiosResponse<SubjectValidationResponse>,
        string,
        AxiosError<GenericErrorResponse>>(POST_SUBJECT_VALIDATION, postSubjectValidation, {
      onSuccess,
      onError,
    });

export const useGetTransactionHistoryListing = (paging: ApiPathParam) =>
    useCustomQuery<CandidateTransactionHistoryListingResponse>(
        GET_CANDIDATE_TRANSACTION_HISTORY,
        () => getTransactionHistoryListing(paging).then((res) => res.data),
        {},
        false,
        false
    );

export const useGetWithdrawal = (uuid = '') =>
    useCustomQuery<GetWithdrawalResponse>(
        GET_EXAM_AMENDMENT_SUMMARY,
        () => getWithdrawal(uuid).then((res) => res.data),
        {},
        false,
        false
    );

export const useGetWithdrawalDropDown = (onSuccess?: () => void) =>
    useCustomQuery<WithdrawalReason[]>(
        GET_EXAM_WITHDRAWAL_DROPDOWN,
        () => getWithdrawalDropDown().then((res) => res.data),
        {onSuccess}
    );

export const usePostWithdrwalUploadDocuments = (
    successCallback: (res: any) => void,
    failCallback?: (res: any) => void
) =>
    useCustomMutate<AxiosResponse<WithdrawalPostUploadDocumentsRequest>,
        WithdrawalPostUploadDocumentsRequest,
        AxiosError<GenericErrorResponse>>(
        POST_EXAM_WITHDRAWAL_UPLOAD_DOCUMENT,
        postWithdrawalUploadDocuments,
        {
          onSuccess: (res) => successCallback(res),
          onError: (err) => failCallback && failCallback(err),
        },
        false,
        false
    );

export const usePutExamWithdrawalDetails = (
    successCallback: (res: any) => void,
    failCallback: (res: any) => void
) =>
    useCustomMutate<AxiosResponse<PutWithdrawalDetailsRequestData>,
        PutWithdrawalDetailsRequestData,
        AxiosError<GenericErrorResponse>>(
        PUT_EXAM_WITHDRAWAL_DETAILS,
        putUpdateExamWithdrawalDetails,
        {
          onSuccess: (res) => successCallback(res),
          onError: (err) => failCallback(err),
        },
        false,
        false
    );

export const usePostCreateExamWithdrawal = (
    onSuccess: (res: any) => void,
    onError: (res: any) => void
) =>
    useCustomMutate(
        POST_CREATE_EXAM_AMENDMENT,
        (csrfToken: string, data: { data: CreateExamWithdrawalRequest; params: TokenOnlyRequest }) =>
            postCreateExamWithdrawal(csrfToken, data.data, data.params),
        {
          onSuccess: (res) => onSuccess(res),
          onError: (err) => onError(err),
        }
    );

export const useGetWithdrawalDraft = (
    onSuccess: (res: GetWithdrawalDraftResponse) => void,
    onError: (res: AxiosError<GenericErrorResponse>) => void,
    uuid = ''
) =>
    useCustomQuery<GetWithdrawalDraftResponse>(
        GET_WITHDRAWAL_DRAFT,
        () => getWithdrawalDraft(uuid).then((res) => res.data),
        {onSuccess, onError, enabled: false},
        false,
        false
    );

export const useGetWithdrawDetails = (uuid: string) =>
    useCustomQuery<WithdrawDetails>(
        GET_WITHDRAW_DETAILS,
        () => getWithdrawDetails(uuid).then((res) => res.data),
        {},
        false,
        false
    );

export const usePutConfirmWithdraw = (
    onSuccess: (res: AxiosResponse) => void,
    onError: (res: AxiosError<GenericErrorResponse>) => void
) =>
    useCustomMutate<AxiosResponse, string, AxiosError<GenericErrorResponse>>(
        PUT_CONFIRM_WITHDRAW,
        putConfirmWithdraw,
        {
          onSuccess,
          onError,
        }
    );

export const useGetWithdrawalAcknowledge = (uuid: string) =>
    useCustomQuery<WithdrawalSubmittedDetails>(
        GET_WITHDRAWAL_ACKNOWLEDGEMENT,
        () => getWithdrawalAcknowledgement(uuid).then((res) => res.data),
        {},
        false,
        false
    );

export const useGetWithdrawalDownloadPDF = (
    uuid: string,
    onSuccess: (download: ExamDownloadSummaryResponse) => void,
    onError: (data: AxiosError) => void
) =>
    useCustomQuery<ExamDownloadSummaryResponse>(
        GET_WITHDRAWAL_DOWNLOAD_PDF,
        () => getWithdrawalDownloadPdf(uuid).then((response) => response.data),
        {
          enabled: false,
          onSuccess,
          onError,
        },
        true,
        false
    );

export const useGetSubmittedWithdrawal = (
    uuid: string,
    onSuccess: (res: GetWithdrawalDraftResponse) => void
) =>
    useCustomQuery<GetWithdrawalDraftResponse>(
        GET_SUBMITTED_WITHDRAWAL,
        () => getSubmittedWithdrawal(uuid).then((res) => res.data),
        {onSuccess},
        false,
        false
    );

export const useGetExamLevelWithdrawal = (
    params: TokenOnlyRequest,
    onError: (res: AxiosError<GenericErrorResponse>) => void,
) =>
    useCustomQuery<ExamLevelResponse>(
        GET_EXAM_LEVEL_WITHDRAWAL,
        () => getExamLevelWithdrawal(params).then((res) => res.data),
        {onError}
    );

export const useGetWithdrawalDraftDateTime = (isNonNational: boolean = false) =>
    useCustomQuery(
        GET_WITHDRAWAL_TIMESTAMP,
        () => getWithdrawalDraftDateTime().then((response) => response.data),
        {enabled: !isNonNational}
    );

export const useGetNonNationPendingExamRegistration = (
    params: CreateExamRegistrationRequest,
    queryOptions?: UseQueryOptions<NonExamRegistrationEligibilityResponse, ServiceError> | undefined
) =>
    useCustomQuery<NonExamRegistrationEligibilityResponse>(
        [GET_PENDING_REGISTRATION, params],
        () => getPendingNonNationalExamRegistration(params).then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const useGetAdmissionExamLevel = (
    params: string,
    queryOptions?: UseQueryOptions<AdmissionExamLevelResponse, ServiceError> | undefined
) =>
    useCustomQuery<AdmissionExamLevelResponse>(
        [GET_ADMISSION_EXAM_LEVEL(params)],
        () => getAdmissionExamLevel(params).then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const usePostCreateNonExamRegistration = (
    onSuccess: (res: AxiosResponse<CreateExamRegistrationResponse>) => void,
    onError: (res: AxiosError<GenericErrorResponse>) => void
) =>
    useCustomMutate<AxiosResponse<CreateExamRegistrationResponse>,
        CreateNonExamRegistrationRequest,
        AxiosError<GenericErrorResponse>>(POST_CREATE_NON_EXAM_REGISTRATION, postCreateNonExamRegistration, {
      onSuccess,
      onError,
    });

export const useGetCEQType = (
    queryOptions?: UseQueryOptions<KeyValueResponse[], ServiceError> | undefined
) =>
    useCustomQuery<KeyValueResponse[]>(
        [GET_CEQ_TYPES],
        () => getCeqTypes().then((res) => res.data),
        queryOptions
    );

export const useGetCEQRequired = (
    queryOptions?: UseQueryOptions<string[], ServiceError> | undefined
) =>
    useCustomQuery<string[]>(
        [GET_CEQ_REQUIRED],
        () => getCeqRequired().then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const useGetParentParticularProfile = (
    uuid: string,
    queryOptions?: UseQueryOptions<ParentParticularDetails, ServiceError> | undefined
) =>
    useCustomQuery<ParentParticularDetails>(
        GET_PARENT_PARTICULAR_PROFILE,
        () => getParentParticularProfile(uuid).then((res) => res.data),
        queryOptions
    );
